// src/lib/player-stats-display.js -- pure display-math for the Stats surface (v1b).
//
// Single job: turn raw player_game_stats rows (from /api/player-stats) into the
// computed values the Stats UI renders -- role detection, valid-sample filtering,
// median default line, hit-rate, and recent-form aggregation. No React, no
// fetching, no side effects (spec v1b-amendment §C2). StatsPage owns state and
// rendering; every calculation lives here and is independently unit-testable.
//
// Field names are the raw DB column keys the route returns verbatim (§G1) -- e.g.
// `sog` not `shots_on_goal`, `rbi` not `rbis`. STAT_DEFINITIONS is the single
// source of truth for labels, role applicability, hit-rate eligibility, value
// type, and fallback lines; nothing outside this module hard-codes a stat field.
import { leagueLabel } from './league-display';

// --- §G3 Stat definition registry ------------------------------------------
// id (unique) | field (raw key) | label | shortLabel | roles | valueType
// (integer|numeric|toi_clock|text) | hitRateEligible | fallbackLine (used when
// fewer than 3 valid samples) | contextOnly (game-log column only, never a chip).
export const STAT_DEFINITIONS = [
  // NHL skater
  { id: 'nhl:sog', field: 'sog', label: 'Shots on Goal', shortLabel: 'SOG', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 2.5 },
  { id: 'nhl:goals', field: 'goals', label: 'Goals', shortLabel: 'G', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nhl:assists', field: 'assists', label: 'Assists', shortLabel: 'A', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nhl:points', field: 'points', label: 'Points', shortLabel: 'P', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nhl:blocks', field: 'blocks', label: 'Blocks', shortLabel: 'BLK', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 1.5 },
  { id: 'nhl:hits', field: 'hits', label: 'Hits', shortLabel: 'HIT', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 1.5 },
  { id: 'nhl:pim', field: 'pim', label: 'Penalty Minutes', shortLabel: 'PIM', roles: ['nhl_skater'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nhl:toi', field: 'toi', label: 'Time on Ice', shortLabel: 'TOI', roles: ['nhl_skater'], valueType: 'toi_clock', hitRateEligible: true, fallbackLine: 18.5 },

  // NHL goalie
  { id: 'nhl:saves', field: 'saves', label: 'Saves', shortLabel: 'SV', roles: ['nhl_goalie'], valueType: 'integer', hitRateEligible: true, fallbackLine: 24.5 },
  { id: 'nhl:ga', field: 'goals_against', label: 'Goals Against', shortLabel: 'GA', roles: ['nhl_goalie'], valueType: 'integer', hitRateEligible: true, fallbackLine: 2.5 },
  { id: 'nhl:svpct', field: 'save_pct', label: 'Save %', shortLabel: 'SV%', roles: ['nhl_goalie'], valueType: 'numeric', hitRateEligible: false, contextOnly: true },

  // MLB batter
  { id: 'mlb:hits', field: 'hits', label: 'Hits', shortLabel: 'H', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'mlb:hr', field: 'home_runs', label: 'Home Runs', shortLabel: 'HR', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'mlb:rbi', field: 'rbi', label: 'RBI', shortLabel: 'RBI', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'mlb:runs', field: 'runs', label: 'Runs', shortLabel: 'R', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'mlb:tb', field: 'total_bases', label: 'Total Bases', shortLabel: 'TB', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 1.5 },
  { id: 'mlb:bk', field: 'batter_strikeouts', label: 'Strikeouts (Batter)', shortLabel: 'K', roles: ['mlb_batter', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },

  // MLB pitcher
  { id: 'mlb:pk', field: 'pitcher_strikeouts', label: 'Strikeouts', shortLabel: 'K', roles: ['mlb_pitcher', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 4.5 },
  { id: 'mlb:er', field: 'earned_runs', label: 'Earned Runs', shortLabel: 'ER', roles: ['mlb_pitcher', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 2.5 },
  { id: 'mlb:ph', field: 'pitcher_hits_allowed', label: 'Hits Allowed', shortLabel: 'H', roles: ['mlb_pitcher', 'mlb_two_way'], valueType: 'integer', hitRateEligible: true, fallbackLine: 4.5 },

  // Context-only (game-log columns, never hit-rate chips)
  { id: 'mlb:ip', field: 'innings_pitched', label: 'IP', shortLabel: 'IP', roles: ['mlb_pitcher', 'mlb_two_way'], valueType: 'text', hitRateEligible: false, contextOnly: true },
  { id: 'mlb:era', field: 'era', label: 'ERA', shortLabel: 'ERA', roles: ['mlb_pitcher', 'mlb_two_way'], valueType: 'text', hitRateEligible: false, contextOnly: true },

  // NFL (#1080) -- the 12 player_game_stats columns from #1040 PR B, one ESPN group per
  // role. Fallback lines are typical book lines. Attempts/targets are context-only
  // columns (a volume signal, not a prop people bet); they still feed the strips below.
  // NFL passer
  { id: 'nfl:pass_yds', field: 'passing_yards', label: 'Passing Yards', shortLabel: 'PaYd', roles: ['nfl_passer'], valueType: 'integer', hitRateEligible: true, fallbackLine: 224.5 },
  { id: 'nfl:pass_td', field: 'passing_touchdowns', label: 'Passing TDs', shortLabel: 'PaTD', roles: ['nfl_passer'], valueType: 'integer', hitRateEligible: true, fallbackLine: 1.5 },
  { id: 'nfl:cmp', field: 'completions', label: 'Completions', shortLabel: 'CMP', roles: ['nfl_passer'], valueType: 'integer', hitRateEligible: true, fallbackLine: 20.5 },
  { id: 'nfl:int', field: 'interceptions_thrown', label: 'Interceptions', shortLabel: 'INT', roles: ['nfl_passer'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nfl:pass_att', field: 'passing_attempts', label: 'Pass Attempts', shortLabel: 'ATT', roles: ['nfl_passer'], valueType: 'integer', hitRateEligible: false, contextOnly: true },
  // NFL rusher
  { id: 'nfl:rush_yds', field: 'rushing_yards', label: 'Rushing Yards', shortLabel: 'RuYd', roles: ['nfl_rusher'], valueType: 'integer', hitRateEligible: true, fallbackLine: 49.5 },
  { id: 'nfl:rush_att', field: 'rushing_attempts', label: 'Rush Attempts', shortLabel: 'CAR', roles: ['nfl_rusher'], valueType: 'integer', hitRateEligible: true, fallbackLine: 12.5 },
  { id: 'nfl:rush_td', field: 'rushing_touchdowns', label: 'Rushing TDs', shortLabel: 'RuTD', roles: ['nfl_rusher'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  // NFL receiver
  { id: 'nfl:rec', field: 'receptions', label: 'Receptions', shortLabel: 'REC', roles: ['nfl_receiver'], valueType: 'integer', hitRateEligible: true, fallbackLine: 4.5 },
  { id: 'nfl:rec_yds', field: 'receiving_yards', label: 'Receiving Yards', shortLabel: 'ReYd', roles: ['nfl_receiver'], valueType: 'integer', hitRateEligible: true, fallbackLine: 49.5 },
  { id: 'nfl:rec_td', field: 'receiving_touchdowns', label: 'Receiving TDs', shortLabel: 'ReTD', roles: ['nfl_receiver'], valueType: 'integer', hitRateEligible: true, fallbackLine: 0.5 },
  { id: 'nfl:tgt', field: 'targets', label: 'Targets', shortLabel: 'TGT', roles: ['nfl_receiver'], valueType: 'integer', hitRateEligible: false, contextOnly: true },
];

// Default recent-form window (last N collected games) -- §E2/§E3.
export const DEFAULT_N = 10;

// --- §I client query normalization ------------------------------------------
// MUST mirror api/lib/player-stats-query.js normalizeQuery/MIN_QUERY_LEN/
// MAX_QUERY_LEN exactly: the client before-send guard has to reach the same
// verdict the server will, so a query that the server would reject never fires a
// request. Lowercase -> strip everything but letters/digits/space and real-name
// punctuation (' . -) which also removes the LIKE wildcards % _ -> collapse
// internal whitespace -> trim.
export const MIN_QUERY_LEN = 2;
export const MAX_QUERY_LEN = 50;
const DISALLOWED_QUERY_CHARS = /[^\p{L}\p{N} .'-]/gu;

export function normalizeStatsQuery(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .toLowerCase()
    .replace(DISALLOWED_QUERY_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// --- §G2 TOI clock converter ------------------------------------------------
// "MM:SS" text -> decimal minutes (e.g. "18:34" -> 18.567). Null/non-string or
// malformed input -> null (callers treat a null value as a non-sample).
export function toiToDecimalMinutes(str) {
  if (str === null || typeof str !== 'string') return null;
  const parts = str.split(':');
  if (parts.length !== 2) return null;
  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);
  if (isNaN(minutes) || isNaN(seconds)) return null;
  return minutes + seconds / 60;
}

// --- §F role detection ------------------------------------------------------
// Role is determined by explicit anchor-field presence, NOT by non-null column
// inference: NHL goalies carry zero-filled skater columns and MLB rows carry
// semantic zeros in role-inapplicable fields, so column inference is wrong for
// both leagues (audit 2026-07-19).
//
// NFL (#1080) is the exception to "one role per row": the writer nulls a whole ESPN
// group when the athlete was absent from it and keeps 0s when present, so null-ness
// IS participation, and a QB row legitimately carries passing AND rushing (an RB
// rushing AND receiving). An NFL row therefore resolves to a SET of roles
// (resolveRowRoles); resolveRole returns the primary one (passer > rusher > receiver)
// for the single-role callers (strip selection). A group counts as participated when
// ANY of its columns is non-null: the parser (src/lib/nfl.js applyStat) nulls PER FIELD
// (ESPN "--" -> undefined -> null), so a single null column -- even the attempt/target
// count -- beside populated siblings is still a game played in that group. The
// per-stat value check in isValidSample/getStatValue still excludes the null column
// itself (pre-Codex Fail-Closed F1).
const NFL_ROW_ROLES = [
  ['nfl_passer', ['passing_attempts', 'completions', 'passing_yards', 'passing_touchdowns', 'interceptions_thrown']],
  ['nfl_rusher', ['rushing_attempts', 'rushing_yards', 'rushing_touchdowns']],
  ['nfl_receiver', ['targets', 'receptions', 'receiving_yards', 'receiving_touchdowns']],
];

// Every role a row participates in (array, insertion order = precedence). NHL/MLB
// rows yield exactly [resolveRole(row)] -- the legacy single-role behaviour.
export function resolveRowRoles(row) {
  if (row.league === 'nfl') {
    const roles = NFL_ROW_ROLES.filter(([, cols]) => cols.some((c) => row[c] != null)).map(([role]) => role);
    return roles.length ? roles : ['nfl_no_participation'];
  }
  return [resolveRole(row)];
}

export function resolveRole(row) {
  // != null (not !== null): an undefined anchor field must read as ABSENT, so a
  // partial/narrowed row never fails open to goalie/two-way (pre-Codex L1).
  const isNHLGoalie = row.saves != null || row.goalie_toi != null;
  const hasBatted = row.at_bats != null;
  const hasPitched = row.innings_pitched != null;

  if (row.league === 'nhl') {
    return isNHLGoalie ? 'nhl_goalie' : 'nhl_skater';
  }
  if (row.league === 'mlb') {
    if (hasBatted && hasPitched) return 'mlb_two_way';
    if (hasBatted) return 'mlb_batter';
    if (hasPitched) return 'mlb_pitcher';
    return 'mlb_no_participation';
  }
  if (row.league === 'nfl') {
    return resolveRowRoles(row)[0];
  }
  return 'unknown';
}

// Union of distinct roles across a player's returned rows (§F). The chip set for
// a player page is derived from this union, so a two-way player who batted in
// some games and pitched in others receives both chip groups -- and an NFL RB who
// carried in some games and was targeted in others gets rusher + receiver.
export function resolveRolesForPlayer(rows) {
  const seen = [];
  for (const row of rows) {
    for (const role of resolveRowRoles(row)) {
      if (!seen.includes(role)) seen.push(role);
    }
  }
  return seen;
}

// Zero-game display role (Issue 2 §6): with NO game rows, role cannot come from
// game-row anchors (resolveRolesForPlayer returns []), so derive it from the roster
// position. MLB: 'P' -> pitcher; 'TWP' (two-way, Ohtani-type) -> two-way so the
// season bar shows BOTH batter and pitcher sets (#1024); everything else (incl.
// null) -> batter. NHL: 'G' -> goalie, else skater. Unknown league -> [] (no bar).
// Applies ONLY in the zero-game state; game-backed players keep resolveRolesForPlayer
// (which already resolves mlb_two_way from at_bats + innings_pitched anchors).
// NFL (#1080): ESPN roster codes -> the group that position's props live in; kickers,
// defenders and null -> [] (no chips, no strip -- nothing to research yet).
const NFL_ZERO_GAME_ROLE = { QB: 'nfl_passer', RB: 'nfl_rusher', FB: 'nfl_rusher', WR: 'nfl_receiver', TE: 'nfl_receiver' };
export function rolesForZeroGame(position, league) {
  if (league === 'mlb') {
    if (position === 'P') return ['mlb_pitcher'];
    if (position === 'TWP') return ['mlb_two_way'];
    return ['mlb_batter'];
  }
  if (league === 'nhl') return position === 'G' ? ['nhl_goalie'] : ['nhl_skater'];
  if (league === 'nfl') {
    return Object.prototype.hasOwnProperty.call(NFL_ZERO_GAME_ROLE, position) ? [NFL_ZERO_GAME_ROLE[position]] : [];
  }
  return [];
}

// --- §G chip set ------------------------------------------------------------
// Chips for a player page: hit-rate-eligible, non-context-only STAT_DEFINITIONS
// whose roles intersect the player's resolved role(s).
export function getChipsForRoles(roles) {
  return STAT_DEFINITIONS.filter(
    (d) => d.hitRateEligible && !d.contextOnly && d.roles.some((r) => roles.includes(r))
  );
}

// --- §G1 value extraction ---------------------------------------------------
// Numeric value of a stat on a row, or null when the column is null/malformed.
// toi_clock text is run through the converter; everything else is coerced to a
// finite number (a real 0 is preserved -- only null/NaN collapse to null).
export function getStatValue(row, statDef) {
  const raw = row[statDef.field];
  if (raw === null || raw === undefined) return null;
  if (statDef.valueType === 'toi_clock') return toiToDecimalMinutes(raw);
  // An empty/whitespace string must not coerce to a real 0 (Number('') === 0),
  // which would enter the denominator as a phantom sample (pre-Codex L2).
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// --- §E1 valid-sample definition -------------------------------------------
// A row is a valid applicable sample for a stat iff (1) the row's resolved role
// is one the stat applies to -- this single check subsumes no-participation rows
// (§A4) and zero-filled cross-role columns (§E1.4), since neither resolves to an
// applicable role -- and (2) the stat value is present (non-null; for TOI, the
// clock text parses).
export function isValidSample(row, statDef) {
  const roles = resolveRowRoles(row);
  if (!statDef.roles.some((r) => roles.includes(r))) return false;
  return getStatValue(row, statDef) !== null;
}

// Valid samples for a stat, newest first, capped at the recent-form window N.
function recentValidSamples(rows, statDef, N = DEFAULT_N) {
  return rows
    .slice()
    .sort((a, b) => String(b.game_date).localeCompare(String(a.game_date)))
    .filter((row) => isValidSample(row, statDef))
    .slice(0, N);
}

// --- §E2 median default line ------------------------------------------------
// Default line for the hit-rate card: the median of the focused stat over the
// most recent N valid samples, rounded to the nearest 0.5. Fewer than 3 valid
// samples -> the stat's static fallback line (never compute a median on <3).
export function computeMedianLine(rows, statDef, N = DEFAULT_N) {
  const samples = recentValidSamples(rows, statDef, N);
  if (samples.length < 3) return statDef.fallbackLine;

  const values = samples.map((row) => getStatValue(row, statDef)).sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);
  const median =
    values.length % 2 === 1 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
  return Math.round(median * 2) / 2;
}

// --- §D hit-rate ------------------------------------------------------------
// "X of Y games cleared" over the most recent N valid samples. Over clears when
// stat > line; Under clears when stat < line. There is no push bucket: an exact
// equal (only possible on a whole-number line for integer stats) is "did not
// clear" (§A3). Every valid applicable sample is in the denominator.
export function computeHitRate(rows, statDef, { mode = 'over', line, N = DEFAULT_N }) {
  const samples = recentValidSamples(rows, statDef, N);
  let cleared = 0;
  for (const row of samples) {
    const v = getStatValue(row, statDef);
    if (mode === 'under' ? v < line : v > line) cleared += 1;
  }
  const total = samples.length;
  const pct = total > 0 ? Math.round((cleared / total) * 100) : 0;
  return { cleared, total, pct };
}

// --- §G1 display value (game-log presentation, not hit-rate math) ------------
// Like getStatValue, but returns the raw "MM:SS" text for TOI so the game log
// shows a human clock, not a decimal. Integer/numeric stats return the number; a
// null/missing column returns null (the log renders "—"). Kept here so the game-
// log component performs no field access or coercion (spec §C1/§C2).
export function getStatDisplayValue(row, statDef) {
  const raw = row[statDef.field];
  if (raw === null || raw === undefined) return null;
  if (statDef.valueType === 'toi_clock') return raw;
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// --- §E3 recent-form strip --------------------------------------------------
// Per-role strip metrics: display key + the STAT_DEFINITIONS id whose field and
// validity drive the per-game rate. mlb_two_way uses the batter strip (at_bats
// is the primary role indicator, §E3).
const RECENT_FORM_METRICS = {
  nhl_skater: [{ key: 'SOG/G', statId: 'nhl:sog' }, { key: 'P/G', statId: 'nhl:points' }, { key: 'A/G', statId: 'nhl:assists' }],
  nhl_goalie: [{ key: 'SV/G', statId: 'nhl:saves' }, { key: 'GA/G', statId: 'nhl:ga' }],
  mlb_batter: [{ key: 'H/G', statId: 'mlb:hits' }, { key: 'TB/G', statId: 'mlb:tb' }, { key: 'RBI/G', statId: 'mlb:rbi' }],
  mlb_pitcher: [{ key: 'K/G', statId: 'mlb:pk' }, { key: 'ER/G', statId: 'mlb:er' }],
  mlb_two_way: [{ key: 'H/G', statId: 'mlb:hits' }, { key: 'TB/G', statId: 'mlb:tb' }, { key: 'RBI/G', statId: 'mlb:rbi' }],
  // NFL (#1080). TGT/G reads a contextOnly definition on purpose: contextOnly only
  // excludes chips (getChipsForRoles); a strip metric is looked up by statId.
  nfl_passer: [{ key: 'PaYd/G', statId: 'nfl:pass_yds' }, { key: 'PaTD/G', statId: 'nfl:pass_td' }, { key: 'CMP/G', statId: 'nfl:cmp' }],
  nfl_rusher: [{ key: 'RuYd/G', statId: 'nfl:rush_yds' }, { key: 'CAR/G', statId: 'nfl:rush_att' }, { key: 'RuTD/G', statId: 'nfl:rush_td' }],
  nfl_receiver: [{ key: 'REC/G', statId: 'nfl:rec' }, { key: 'ReYd/G', statId: 'nfl:rec_yds' }, { key: 'TGT/G', statId: 'nfl:tgt' }],
};

const defById = (id) => STAT_DEFINITIONS.find((d) => d.id === id);

// Per-game rates for a player's resolved role. Returns null for roles with no
// strip (mlb_no_participation / unknown) so the caller can show "No games in
// this role collected yet." Each metric's denominator is the count of valid
// applicable samples for THAT metric (not all returned rows); a metric with zero
// valid samples reports value null (no divide-by-zero).
export function computeRecentFormStrip(rows, role, N = DEFAULT_N) {
  const metrics = RECENT_FORM_METRICS[role];
  if (!metrics) return null;

  return metrics.map(({ key, statId }) => {
    const statDef = defById(statId);
    const samples = recentValidSamples(rows, statDef, N);
    if (samples.length === 0) {
      return { key, label: statDef.label, value: null, sampleCount: 0 };
    }
    const sum = samples.reduce((acc, row) => acc + getStatValue(row, statDef), 0);
    return {
      key,
      label: statDef.label,
      value: Math.round((sum / samples.length) * 10) / 10,
      sampleCount: samples.length,
    };
  });
}

// Roles that have a recent-form strip (the keys of RECENT_FORM_METRICS).
const STRIP_ROLES = new Set(Object.keys(RECENT_FORM_METRICS));

// League display label lives in league-display.js (derived from the key -- no
// per-league table, so it cannot drift from SUPPORTED_LEAGUES). Re-exported
// here so SearchResults.jsx and the existing tests keep their import path; the
// untrusted-key behaviour ('constructor' -> 'CONSTRUCTOR', null -> '') is
// unchanged and still pinned by player-stats-display.test.js.
export { leagueLabel };

// Position display label -- MUST be league-aware. `C` collides across leagues:
// NHL C = Center, MLB C = Catcher (which displays as the bare "C"). A league-blind
// map would mislabel every MLB catcher as "Center" (#849 R3 P1). Only NHL codes map
// to words; MLB (and any non-nhl league) render the raw roster code unchanged
// (C=Catcher, SS, 1B, P, ...). Own-property guarded like leagueLabel so an unknown/
// poisoned NHL code (e.g. "constructor") can never resolve to an Object.prototype
// member -- it returns the raw value. Empty/null position -> '' so the subtitle
// filter-join drops the segment (no dangling separator).
const NHL_POSITION_LABEL = { C: 'Center', L: 'Left Wing', R: 'Right Wing', D: 'Defense', G: 'Goalie' };
export function positionLabel(position, league) {
  if (!position) return '';
  if (league === 'nhl') {
    if (Object.prototype.hasOwnProperty.call(NHL_POSITION_LABEL, position)) return NHL_POSITION_LABEL[position];
    return position;
  }
  return position;
}

// Roster-status badge (Issue 2 §6). Active/null/non-string -> no badge. Injured* ->
// "IL". Optioned / "Reassigned to Minors" -> "Optioned". Any other non-empty string
// renders raw (fail-open on display). Returns null (no badge) or { label, title }.
export function rosterStatusBadge(status) {
  if (typeof status !== 'string' || status.trim() === '' || status === 'Active') return null;
  if (/injured/i.test(status)) return { label: 'IL', title: status };
  if (/option|minors/i.test(status)) return { label: 'Optioned', title: status };
  return { label: status, title: status };
}

// Official headshot URL by player_id (NHL API id / MLB person.id -- the same id on
// search rows + game-log rows). Own-property guarded like positionLabel so an unknown/
// poisoned league can't resolve a prototype member. null -> caller falls back to initials.
const HEADSHOT_URL = {
  nhl: (id) => `https://assets.nhle.com/mugs/nhl/latest/${id}.png`,
  mlb: (id) => `https://midfield.mlbstatic.com/v1/people/${id}/spots/120`,
  // ESPN athlete id (the NFL player_id written by the collector, #1040 PR B).
  nfl: (id) => `https://a.espncdn.com/i/headshots/nfl/players/full/${id}.png`,
};
export function headshotUrl(playerId, league) {
  // Validate playerId as a positive integer before interpolating it into the CDN path
  // (defense-in-depth: player_id is a server-validated bigint on every real path, but treat
  // DB/deep-link values as hostile -- a garbage id would otherwise build a bad/traversal path;
  // null here just falls back to initials). Mirrors the roster-sync positive-int contract.
  const id = Number(playerId);
  if (!Number.isInteger(id) || id <= 0) return null;
  if (Object.prototype.hasOwnProperty.call(HEADSHOT_URL, league)) return HEADSHOT_URL[league](id);
  return null;
}

// Game-result badge color. Own-property guarded for the same reason as leagueLabel:
// result is a route value (§B1.3 promises only "W"/"L"/null, but the client must
// not trust that) and an unguarded lookup on e.g. "constructor" would resolve to an
// Object.prototype member and be assigned as a CSS color (pre-Codex trust-boundary fix).
const RESULT_COLOR = { W: '#4ade80', L: '#f87171' };
export function resultColor(result) {
  if (Object.prototype.hasOwnProperty.call(RESULT_COLOR, result)) return RESULT_COLOR[result];
  return '#64748b';
}

// --- orchestration helpers (moved out of StatsPage, P1#1) -------------------
// The player's primary strip role: the most frequent role across the game log
// that actually has a strip. Falls back to the first resolved role (which yields
// a null strip for no-participation/unknown) or 'unknown' for an empty log.
export function resolvePrimaryRole(rows) {
  const counts = {};
  for (const row of rows) {
    const role = resolveRole(row);
    counts[role] = (counts[role] || 0) + 1;
  }
  const withStrip = Object.keys(counts).filter((r) => STRIP_ROLES.has(r));
  if (withStrip.length) return withStrip.sort((a, b) => counts[b] - counts[a])[0];
  return resolveRolesForPlayer(rows)[0] || 'unknown';
}

// Honest recent-form caption: per-game rates over the collected window, with the
// date range. Never implies season totals (§7/§8). rows are newest-first.
export function buildStripCaption(rows) {
  if (!rows.length) return '';
  // Count spans ALL returned rows so it agrees with the full-window date range
  // (§E2). Clamping the count to N while the dates span every row read as
  // "over 10 games" beside a 25-game span -- internally inconsistent (pre-Codex).
  return `over ${rows.length} collected games · ${rows[0].game_date}–${rows[rows.length - 1].game_date}`;
}

// Jersey display segment (#998 PR 3): '#N' for a present jersey, null when absent.
// rosters.jersey_number is text (digit string); tolerate a number, strip any stray
// '#'/whitespace so the segment never renders as a bare '#' or '##N'. Display-only.
function jerseySegment(v) {
  if (v == null) return null;
  const s = String(v).trim().replace(/^#+/, '').trim();
  return s ? '#' + s : null;
}

// Header subtitle -- ADAPTIVE 3-max (ratified 2026-08-13, #998 PR 3). League is a
// FALLBACK segment: shown only when both position and jersey are absent, so a rich
// header never exceeds TEAM · POSITION · #JERSEY (parent mockup EDM · Center · #97)
// and a degraded one (unrostered / goalie / offseason) never drops below TEAM · LEAGUE.
// filter(Boolean) drops any null segment cleanly (no dangling separator).
export function playerSubtitle(player) {
  const position = positionLabel(player?.position, player?.league);
  const jersey = jerseySegment(player?.jersey_number);
  const primary = [player?.team, position, jersey].filter(Boolean);
  const showLeague = !position && !jersey && player?.league;
  return (showLeague ? [...primary, leagueLabel(player.league)] : primary).join(' · ');
}

// Player display identity: prefer the selected candidate; when it lacks a name
// (v1c deep-link opens by id only), derive name/team/league from the first game
// row. Returns the selection unchanged when there is nothing to derive from.
export function derivePlayerDisplay(selectedPlayer, rows) {
  if (selectedPlayer?.name) return selectedPlayer;
  // v1c deep-link (id-only) opens with no name -> derive from the first game row,
  // forwarding position (#849 P1 #3) and jersey_number (#998 PR 3) so the header
  // shows them on the deep-link path too. get_player_game_log returns both as
  // per-request scalars; `?? null` degrades a missing roster match to null (header
  // shows TEAM · LEAGUE).
  if (rows[0]) return { name: rows[0].player_name, team: rows[0].team, league: rows[0].league, position: rows[0].position ?? null, jersey_number: rows[0].jersey_number ?? null, player_id: rows[0].player_id ?? null };
  return selectedPlayer;
}

// --- PR4 season-stats bar (player_season_stats row -> display rows) ---------
// Format a season-stat value for the bar, or null to omit the cell. Numeric
// columns arrive from PostgREST as strings, so coerce (mirrors getStatValue's
// Number() pattern) before formatting; a null/undefined/non-finite source omits
// the entry entirely rather than rendering a blank or "NaN" cell.
function formatSeasonValue(value, kind) {
  if (value === null || value === undefined) return null;
  // An empty/whitespace string must not coerce to a real 0 (Number('') === 0) and
  // render a phantom cell -- mirrors getStatValue's real-zero guard.
  if (typeof value === 'string' && value.trim() === '') return null;
  // 'raw' passes the source through verbatim (e.g. innings_pitched "6.1" baseball
  // notation -- 6 innings + 1 out -- which numeric formatting would misrender as a
  // decimal). Trim strings; coerce a number to its string form (Issue 2 §6).
  if (kind === 'raw') {
    const str = typeof value === 'string' ? value.trim() : String(value);
    return str === '' ? null : str;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  switch (kind) {
    case 'integer': return String(Math.round(n)); // never toFixed
    case 'd1': return n.toFixed(1);
    case 'd2': return n.toFixed(2);
    case 'd3': return n.toFixed(3);
    default: return null;
  }
}

// Build a display cell, dropping it when the source value is missing/invalid.
function seasonRow(label, value, kind) {
  const valueStr = formatSeasonValue(value, kind);
  return valueStr === null ? null : { label, valueStr };
}

// W-L cell (Issue 2 §6): "W-L" from two integer columns; null (dropped downstream)
// when either is missing/invalid so the bar never shows a half-populated record.
function seasonWinLoss(wins, losses) {
  const w = formatSeasonValue(wins, 'integer');
  const l = formatSeasonValue(losses, 'integer');
  return w === null || l === null ? null : { label: 'W-L', valueStr: `${w}-${l}` };
}

// Ordered [{label,valueStr}] cells for the season-stats bar, derived from a
// current-season player_season_stats row and the player's resolved role(s).
// Pure -- no React, no fetching. Field names per player-season-stats.md §5 /
// v1b-amendment §G. roles may be a Set or an array (StatsPage passes the array
// from resolveRolesForPlayer); either is accepted. A player matching multiple
// role branches (mlb_two_way) receives every applicable group, batter before
// pitcher. Returns [] for a null row or an empty role set.
export function getSeasonStatRows(seasonStats, roles) {
  if (!seasonStats || typeof seasonStats !== 'object') return [];
  // Accept a Set or an array; anything else fails closed to [] rather than
  // throwing inside StatsPage's useMemo (a throw there white-screens the page).
  if (!(roles instanceof Set) && !Array.isArray(roles)) return [];
  const roleSet = roles instanceof Set ? roles : new Set(roles);
  if (roleSet.size === 0) return [];

  const s = seasonStats;
  const rows = [];

  if (roleSet.has('nhl_skater')) {
    rows.push(seasonRow('GP', s.games_played, 'integer'));
    rows.push(seasonRow('G', s.goals, 'integer'));
    rows.push(seasonRow('A', s.assists, 'integer'));
    rows.push(seasonRow('PTS', s.points, 'integer'));
    // SOG/G is computed -> guard the divisor explicitly (no div/0, no phantom cell).
    const gp = Number(s.games_played);
    if (s.sog != null && s.games_played != null && Number.isFinite(gp) && gp > 0) {
      rows.push(seasonRow('SOG/G', Number(s.sog) / gp, 'd1'));
    }
  }

  if (roleSet.has('nhl_goalie')) {
    rows.push(seasonRow('GP', s.games_played, 'integer'));
    rows.push(seasonRow('W', s.wins, 'integer'));
    rows.push(seasonRow('GAA', s.goals_against_avg, 'd2'));
    rows.push(seasonRow('SV%', s.save_pct, 'd3'));
  }

  if (roleSet.has('mlb_batter') || roleSet.has('mlb_two_way')) {
    // Issue 2 §6 (G6): extended batter set for ALL players -- AVG, HR, RBI, H, R, SB.
    rows.push(seasonRow('AVG', s.batting_avg, 'd3'));
    rows.push(seasonRow('HR', s.home_runs, 'integer'));
    rows.push(seasonRow('RBI', s.rbi, 'integer'));
    rows.push(seasonRow('H', s.hits, 'integer'));
    rows.push(seasonRow('R', s.runs, 'integer'));
    rows.push(seasonRow('SB', s.stolen_bases, 'integer'));
  }

  if (roleSet.has('mlb_pitcher') || roleSet.has('mlb_two_way')) {
    // Issue 2 §6 (G6): extended pitcher set -- ERA, W-L, K, IP, WHIP. IP is raw
    // baseball notation (text); W-L joins two integer columns.
    rows.push(seasonRow('ERA', s.era, 'd2'));
    rows.push(seasonWinLoss(s.wins, s.losses));
    rows.push(seasonRow('K', s.pitcher_strikeouts, 'integer'));
    rows.push(seasonRow('IP', s.innings_pitched, 'raw'));
    rows.push(seasonRow('WHIP', s.whip, 'd2'));
  }

  // Drop null cells and de-dupe by label: a mixed role union (skater + goalie
  // game rows) emits 'GP' from both branches -> a duplicate React key and a
  // duplicated column. Keep the first occurrence of each label.
  const seen = new Set();
  return rows.filter((r) => r && !seen.has(r.label) && seen.add(r.label));
}

// Human-readable season label from a player_season_stats row. NHL seasons store
// the 4-digit start year ('2025' -> '2025-26'); MLB stores the calendar year
// ('2026' -> '2026'). Null row or falsy season -> '' (the bar renders nothing).
export function deriveSeasonLabel(seasonStats) {
  if (!seasonStats || !seasonStats.season) return '';
  const season = seasonStats.season;
  if (seasonStats.league === 'nhl') {
    return `${season}-${String(parseInt(season, 10) + 1).slice(2)}`;
  }
  if (seasonStats.league === 'mlb') {
    return String(season);
  }
  return String(season);
}
